import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Tab, Tabs } from '@mui/material';
import { useAuthStore } from '@/stores/authStore';
import { useIsMobile } from '@/hooks/useIsMobile';
import { WorkOrderCalendarTab } from './WorkOrderCalendarTab';
import { ShiftCalendarTab } from './ShiftCalendarTab';
import { LiftCalendarTab } from './LiftCalendarTab';
import { CourtesyCalendarTab } from './CourtesyCalendarTab';
import { SupervisorTaskCalendarTab } from './SupervisorTaskCalendarTab';
import {
  getAllowedCalendarTabs,
  resolveCalendarTab,
  type CalendarTab,
} from './calendarTabs';

export default function CalendarPage() {
  const { t } = useTranslation('calendar');
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();
  // On desktop the calendar page is rendered full-bleed by AppShell: the page
  // fills the viewport below the header and each tab's calendar stretches to a
  // 16px gutter from the browser edges. Mobile keeps the default scrolling
  // layout with the fixed-height calendars.
  const fill = !isMobile;

  // Työmääräimet, Työvuorot and Nosturit calendars are consolidated into a
  // single page with URL-driven tabs (?tab=…). Shifts has no dedicated read
  // permission (the shift calendar is open to any authenticated user today),
  // so this route is intentionally ungated at the router level — the active
  // tab is clamped to the tabs the user may actually see, so a crafted
  // ?tab= for a permission the user lacks can never leave the MUI Tabs
  // component on an unmatched value (blank content).
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canViewWorkOrders = hasPermission('work_orders.read');
  const canViewLifts = hasPermission('settings.view');
  const canViewCourtesy = hasPermission('work_orders.read');
  // Supervisor task layer (#3671): the foreman's permission, also for a direct ?tab=tasks URL.
  const canViewSupervisorTasks = hasPermission('scheduling.exceptions');
  const allowedTabs = getAllowedCalendarTabs({
    canViewWorkOrders,
    canViewLifts,
    canViewCourtesy,
    canViewSupervisorTasks,
  });
  const activeTab = resolveCalendarTab(searchParams.get('tab'), allowedTabs);

  const handleTabChange = useCallback((_: React.SyntheticEvent, value: CalendarTab) => {
    const next = new URLSearchParams(searchParams);
    // 'workorders' is the default tab and carries no ?tab param so its URL stays clean.
    if (value === 'workorders') {
      next.delete('tab');
    } else {
      next.set('tab', value);
    }
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  return (
    <Box
      sx={
        fill
          ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }
          : { p: { xs: 2, sm: 3 } }
      }
    >
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
      >
        {canViewWorkOrders && <Tab value="workorders" label={t('tabs.workorders')} />}
        <Tab value="shifts" label={t('tabs.shifts')} />
        {canViewLifts && <Tab value="lifts" label={t('tabs.lifts')} />}
        {canViewCourtesy && <Tab value="courtesy" label={t('tabs.courtesy')} />}
        {canViewSupervisorTasks && <Tab value="tasks" label={t('tabs.tasks')} />}
      </Tabs>

      <Box
        sx={
          fill
            ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }
            : undefined
        }
      >
        {activeTab === 'workorders' && canViewWorkOrders && <WorkOrderCalendarTab fillHeight={fill} />}
        {activeTab === 'shifts' && <ShiftCalendarTab fillHeight={fill} />}
        {activeTab === 'lifts' && canViewLifts && <LiftCalendarTab fillHeight={fill} />}
        {activeTab === 'courtesy' && canViewCourtesy && <CourtesyCalendarTab fillHeight={fill} />}
        {activeTab === 'tasks' && canViewSupervisorTasks && <SupervisorTaskCalendarTab fillHeight={fill} />}
      </Box>
    </Box>
  );
}
